import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  makeStyles,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Grid,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import StripeCheckout from './StripeCheckout/StripeCheckout';
import ComplianceBadges from './ComplianceBadges';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: '#ffffff',
    padding: theme.spacing(4, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  headerRow: {
    width: '100%',
    maxWidth: 820,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  heading: { fontWeight: 700, fontSize: '1.6rem' },
  sub: { color: '#4b5563' },

  card: {
    width: '100%',
    maxWidth: 820,
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    background: '#ffffff',
    padding: theme.spacing(2),
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },

  plansGrid: {
    marginTop: theme.spacing(1),
  },

  planCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'box-shadow .15s ease, transform .05s ease, border-color .15s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    '&:hover': {
      boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
    },
  },
  planCardSelected: {
    border: '1.5px solid #111827',
    boxShadow: '0 10px 22px rgba(0,0,0,0.08)',
    transform: 'translateY(-1px)',
  },

  planLabel: { fontWeight: 700, fontSize: '1rem', color: '#111827' },
  planDesc: { color: '#4b5563', marginTop: 4, fontSize: '.9rem' },
  planPrice: { marginTop: 8, fontWeight: 700, color: '#111827' },

  dividerNote: {
    marginTop: theme.spacing(2),
    color: '#6b7280',
    fontSize: 13,
    textAlign: 'center',
  },

  payWrap: {
    marginTop: theme.spacing(2),
    borderTop: '1px dashed #e5e7eb',
    paddingTop: theme.spacing(2),
  },

  termsLink: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
    '& > button': {
      textTransform: 'none',
    },
  },
}));

// SAFE service names + your exact price points
const supportPlans = [
  { id: 0, label: 'Starter', description: 'Starter Support • Flexible', price: 10, currency: 'eur' },
  { id: 1, label: 'Beginner', description: 'Starter Support • 1 Month', price: 15, currency: 'eur' },
  // { id: 2, label: 'Essential', description: 'Essential Support • 12 Months', price: 40 },
  { id: 3, label: 'Silver', description: 'Standard Support • 12 Months', price: 50, currency: 'eur' },
  // { id: 4, label: 'Advanced', description: 'Advanced Support • 12 Months', price: 60 },
  { id: 5, label: 'Premium', description: 'Professional Support • 12 Months', price: 75, currency: 'eur' },
  // { id: 6, label: 'Premium', description: 'Premium Support • 12 Months', price: 80 },
  { id: 7, label: 'Platinum', description: 'Enterprise Support • 12 Months', price: 90, currency: 'eur' },
];

const creditPackages = [
  { id: 8, label: '120 Credits Package', description: 'Training Guidance • 120 Credits', price: 210, currency: 'eur' },
  { id: 9, label: '240 Credits Package', description: 'Training Guidance • 240 Credits', price: 400, currency: 'eur' },
  { id: 10, label: '360 Credits Package', description: 'Training Guidance • 360 Credits', price: 500, currency: 'eur' },
  { id: 11, label: 'Premium Package', description: 'Premium Support', price: 349, currency: 'usd' },
  { id: 12, label: 'Elite Package', description: 'Elite Support', price: 499, currency: 'usd' },
];

const allPlans = [...supportPlans, ...creditPackages];

const Edmond = () => {
  const classes = useStyles();
  const [selected, setSelected] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTab, setInfoTab] = useState(0); // 0 = About, 1 = Terms
  const [planTab, setPlanTab] = useState(0); // 0 = Support Plans, 1 = Credit Packages
  const selectedItem = allPlans.find((s) => s.id === selected);
  const paymentSectionRef = React.useRef(null);

  const handlePlanSelect = (planId) => {
    setSelected(planId);
    // Scroll to payment section after a short delay to allow render
    setTimeout(() => {
      if (paymentSectionRef.current) {
        paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <Box className={classes.page}>
      <div className={classes.headerRow}>
        <div>
          <Typography className={classes.heading}>Service Plans & Online Support</Typography>
          <Typography className={classes.sub}>Minimal, secure checkout. Terms required before payment.</Typography>
        </div>
        <Tooltip title='About services & terms'>
          <IconButton
            onClick={() => {
              setInfoTab(0);
              setInfoOpen(true);
            }}>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </div>

      <ComplianceBadges />

      <Paper className={classes.card} elevation={0}>
        <Typography align='center' style={{ fontWeight: 700, marginBottom: 16 }}>
          Choose a plan
        </Typography>

        <Tabs
          value={planTab}
          onChange={(_, v) => setPlanTab(v)}
          indicatorColor='primary'
          textColor='primary'
          variant='fullWidth'
          style={{ marginBottom: 16 }}>
          <Tab label='Support Plans' />
          <Tab label='Credit Packages' />
        </Tabs>

        {planTab === 0 && (
          <Grid container spacing={2} className={classes.plansGrid}>
            {/* First row - Starter and Beginner centered */}
            <Grid container item xs={12} spacing={2} style={{ display: 'flex', justifyContent: 'center' }}>
              {supportPlans.slice(0, 2).map((p) => {
                const isSelected = selected === p.id;
                return (
                  <Grid key={p.id} item xs={12} sm={6} md={4}>
                    <Paper
                      elevation={0}
                      className={`${classes.planCard} ${isSelected ? classes.planCardSelected : ''}`}
                      onClick={() => handlePlanSelect(p.id)}>
                      <Typography className={classes.planLabel}>{p.label}</Typography>
                      <Typography className={classes.planDesc}>{p.description}</Typography>
                      <Typography className={classes.planPrice}>
                        {p.currency === 'usd' ? '$' : '€'}
                        {p.price}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Second row - Silver, Premium, and Platinum */}
            <Grid container item xs={12} spacing={2}>
              {supportPlans.slice(2).map((p) => {
                const isSelected = selected === p.id;
                return (
                  <Grid key={p.id} item xs={12} sm={6} md={4}>
                    <Paper
                      elevation={0}
                      className={`${classes.planCard} ${isSelected ? classes.planCardSelected : ''}`}
                      onClick={() => handlePlanSelect(p.id)}>
                      <Typography className={classes.planLabel}>{p.label}</Typography>
                      <Typography className={classes.planDesc}>{p.description}</Typography>
                      <Typography className={classes.planPrice}>
                        {p.currency === 'usd' ? '$' : '€'}
                        {p.price}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}

        {planTab === 1 && (
          <Grid container spacing={2} className={classes.plansGrid}>
            {creditPackages.map((p) => {
              const isSelected = selected === p.id;
              return (
                <Grid key={p.id} item xs={12} sm={6} md={4}>
                  <Paper
                    elevation={0}
                    className={`${classes.planCard} ${isSelected ? classes.planCardSelected : ''}`}
                    onClick={() => handlePlanSelect(p.id)}>
                    <Typography className={classes.planLabel}>{p.label}</Typography>
                    <Typography className={classes.planDesc}>{p.description}</Typography>
                    <Typography className={classes.planPrice}>
                      {p.currency === 'usd' ? '$' : '€'}
                      {p.price}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Typography className={classes.dividerNote}>
          Select a plan to continue. The Pay button stays grey until you accept terms in the form.
        </Typography>

        <div className={classes.termsLink}>
          <Button
            color='primary'
            onClick={() => {
              setInfoTab(1);
              setInfoOpen(true);
            }}>
            View Terms & Conditions
          </Button>
        </div>

        {selectedItem &&
          (() => {
            const basePrice = selectedItem.price;
            const tax = 0;
            const total = basePrice + tax;

            return (
              <div ref={paymentSectionRef} className={classes.payWrap}>
                <Typography align='center' style={{ color: '#374151' }}>
                  {selectedItem.description}
                </Typography>

                {/* Stripe form handles the disabled Pay state until terms are accepted */}
                <div style={{ marginTop: 12 }}>
                  <StripeCheckout
                    price={total}
                    description={selectedItem.description}
                    basePrice={basePrice}
                    tax={tax}
                    autoCheckTerms={true}
                    currency={selectedItem.currency || 'eur'}
                  />
                </div>
              </div>
            );
          })()
          
}
      </Paper>

      {/* Info / Terms dialog */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Service Information</DialogTitle>
        <Tabs value={infoTab} onChange={(_, v) => setInfoTab(v)} indicatorColor='primary' textColor='primary' variant='fullWidth'>
          <Tab label='About the Services' />
          <Tab label='Terms & Conditions' />
        </Tabs>
        <DialogContent dividers>
          {infoTab === 0 && (
            <>
              <Typography variant='h6' gutterBottom>
                What you get
              </Typography>
              <Typography paragraph>
                Professional remote <strong>computer support</strong> and <strong>online programming guidance</strong>. Sessions use video call or
                screen-share. We help with setup, troubleshooting, optimization, code reviews, and developer mentorship.
              </Typography>
              <Typography variant='h6' gutterBottom>
                How it works
              </Typography>
              <Typography component='div'>
                <ul>
                  <li>Select the plan that fits your needs.</li>
                  <li>Complete payment securely via Stripe.</li>
                  <li>We’ll follow up by email to schedule your session(s).</li>
                </ul>
              </Typography>
              <Typography variant='h6' gutterBottom>
                Support scope
              </Typography>
              <Typography component='div'>
                <ul>
                  <li>PC/Mac software setup, performance checks, and troubleshooting.</li>
                  <li>Programming help: concepts, debugging, code structure, reviews.</li>
                  <li>No physical goods, on-site work, or restricted services.</li>
                </ul>
              </Typography>
            </>
          )}
          {infoTab === 1 && (
            <>
              <Typography variant='h6' gutterBottom>
                Terms (summary)
              </Typography>
              <Typography component='div'>
                <ul>
                  <li>Remote services only; no physical products.</li>
                  <li>Sessions are scheduled by email after payment.</li>
                  <li>Free reschedule/cancel ≥ 24h before; within 24h a fee up to 100% may apply.</li>
                  <li>Completed time is non-refundable; unused prepaid time can be rescheduled within 60 days.</li>
                  <li>
                    Use must comply with{' '}
                    <a href='https://stripe.com/restricted-businesses' target='_blank' rel='noopener noreferrer'>
                      Stripe’s Restricted Businesses
                    </a>
                    .
                  </li>
                  <li>
                    Payments processed by Stripe; see{' '}
                    <a href='https://stripe.com/privacy' target='_blank' rel='noopener noreferrer'>
                      Stripe Privacy
                    </a>
                    .
                  </li>
                </ul>
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Full Terms & Privacy are also shown inside the payment form.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Edmond;
